import { Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  formatEuro,
  LIBRARY_STATUS_LABELS,
  LIBRARY_STATUSES,
  LIBRARY_UNIT_LABELS,
  LIBRARY_UNITS,
  PRACTICE_MIN_ORGS,
  type LibraryDetailResponse,
  type LibraryElementBody,
  type LibraryImportResult,
  type LibraryListResponse,
  type LibraryListRow,
  type LibraryStatus,
  type LibraryUnit,
} from "@klantpunt/shared";
import { IconChevronLeft, IconDownload, IconUpload } from "../app/icons";
import { Modal } from "../app/Modal";
import { api, ApiError } from "../lib/api";
import { useMe } from "../lib/auth";
import { useToast } from "../lib/toast";
import { parseEuro } from "./Platform";
import { Segmented } from "../app/Choice";

/**
 * Kennisbank in platformbeheer (docs/ASSETS-MJOP.md, "Kennisbank"): per NL-SfB-code levensduur,
 * en vervangingsprijs. Standaard de codes die in gebruik zijn zonder gegevens.
 */
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const euroInput = (c: number | null) => (c == null ? "" : (c / 100).toFixed(2).replace(".", ","));
const intOrNull = (v: string) => (v.trim() ? Number(v.trim()) : null);
const validInt = (v: string) => !v.trim() || /^\d+$/.test(v.trim());
const LIST_KEY = ["platform", "library"];
const detailKey = (code: string) => ["platform", "library", code];

type Filter = "missing" | "filled" | "all";
const FILTERS: [Filter, string][] = [
  ["missing", "In gebruik zonder gegevens"],
  ["filled", "Ingevuld"],
  ["all", "Alle codes"],
];
const PAGE = 100;

function StatusChip({ status }: { status: LibraryStatus }) {
  return <span className={`chip lib-${status}`}>{LIBRARY_STATUS_LABELS[status]}</span>;
}

export function LibraryPage() {
  const { data: me } = useMe();
  if (!me?.user.platformAdmin)
    return (
      <div className="center">
        <p>Deze pagina bestaat niet.</p>
      </div>
    );
  return <Library />;
}

function Library() {
  const q = useQuery({ queryKey: LIST_KEY, queryFn: () => api<LibraryListResponse>("/platform/library") });
  const [filter, setFilter] = useState<Filter>("missing");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<string | null>(null);
  const [importing, setImporting] = useState<{ file: string; name: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const rows = useMemo(() => {
    const all = q.data?.rows ?? [];
    const s = search.trim().toLowerCase();
    return all
      .filter((r) => (filter === "missing" ? r.assets > 0 && !r.covered : filter === "filled" ? r.filled : true))
      .filter((r) => !s || r.code.startsWith(s) || r.label.toLowerCase().includes(s))
      .sort(
        (a, b) =>
          (filter === "missing" ? b.assets - a.assets : 0) || a.code.localeCompare(b.code, "nl", { numeric: true }),
      );
  }, [q.data, filter, search]);

  const t = q.data?.totals;
  const pct = t && t.assets ? Math.round((t.coveredAssets / t.assets) * 100) : 0;

  const pickFile = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 7 * 1024 * 1024) return toast("Dit bestand is te groot (max. 7 MB)");
    const buf = new Uint8Array(await f.arrayBuffer());
    let bin = "";
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    setImporting({ file: btoa(bin), name: f.name });
  };

  return (
    <div className="page platform">
      <Link to="/platform" className="backlink">
        <IconChevronLeft size={18} aria-hidden="true" />
        Organisaties
      </Link>
      <p className="label">Platformbeheer</p>
      <div className="titlerow">
        <h1 className="dot">Kennisbank</h1>
        <div className="btnrow pf-wrap">
          <a className="btn" href="/api/platform/library/export.xlsx" download>
            <IconDownload size={20} aria-hidden="true" />
            Exporteren naar Excel
          </a>
          <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
            <IconUpload size={20} aria-hidden="true" />
            Importeren uit Excel
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            tabIndex={-1}
            aria-label="Excel-bestand kiezen"
            onChange={(e) => {
              void pickFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </div>
      </div>
      <p className="muted small">
        Per NL-SfB-code levensduur en vervangingsprijs. Een subcode erft per veld van de bovenliggende code. Wijzigingen
        raken bestaande assets niet. Versie: NLBE-SfB concept 2025.
      </p>
      <div className="pf-kpis" role="group" aria-label="Voortgang">
        <span className="pf-kpi">
          <span className="label">Codes ingevuld</span>
          <strong className="num" data-testid="lib-filled">
            {t ? `${t.filled} van ${t.codes}` : "—"}
          </strong>
        </span>
        <span className="pf-kpi">
          <span className="label">Assets gedekt</span>
          <strong className="num" data-testid="lib-covered">
            {t ? `${pct}%` : "—"}
          </strong>
          {t && (
            <span className="muted small">
              {t.coveredAssets} van {t.assets} assets
            </span>
          )}
        </span>
      </div>

      <div className="lib-tools">
        <Segmented
          label="Filter kennisbank"
          items={FILTERS.map(([f, label]) => ({ value: f, label }))}
          value={filter}
          onChange={(f) => {
            setFilter(f);
            setLimit(PAGE);
          }}
        />
        <label className="field lib-search">
          <span className="sr-only">Zoek op code of omschrijving</span>
          <input
            type="search"
            placeholder="Zoek op code of omschrijving"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setLimit(PAGE);
            }}
          />
        </label>
      </div>

      {
        <section className="panel" aria-busy={q.isPending}>
          {q.isPending ? (
            <Loading />
          ) : !rows.length ? (
            <p className="muted">
              {filter === "missing" ? "Alle codes die in gebruik zijn hebben gegevens." : "Geen codes gevonden."}
            </p>
          ) : (
            <>
              <div className="tscroll">
                <table className="it lib-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Omschrijving</th>
                      <th className="num">Assets</th>
                      <th>Levensduur</th>
                      <th>Vervanging</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, limit).map((r) => (
                      <Row key={r.code} r={r} onOpen={() => setOpen(r.code)} />
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > limit && (
                <button type="button" className="btn ghost" onClick={() => setLimit(limit + PAGE)}>
                  Toon meer ({rows.length - limit})
                </button>
              )}
            </>
          )}
        </section>
      }

      {open && <CodeDialog code={open} onClose={() => setOpen(null)} />}
      {importing && <ImportDialog file={importing.file} name={importing.name} onClose={() => setImporting(null)} />}
    </div>
  );
}

function Row({ r, onOpen }: { r: LibraryListRow; onOpen: () => void }) {
  const e = r.element;
  return (
    <tr>
      <td>
        <button type="button" className="linkbtn num" onClick={onOpen} aria-label={`${r.code} ${r.label} openen`}>
          {r.code}
        </button>
      </td>
      <td className={r.level === 2 ? "" : "muted"}>{r.label}</td>
      <td className="num">{r.assets || "—"}</td>
      <td>{e?.lifespanYears != null ? `${e.lifespanYears} jaar` : "—"}</td>
      <td>
        {e?.replacementPriceCents != null
          ? `${formatEuro(e.replacementPriceCents)}${e.unit ? ` / ${LIBRARY_UNIT_LABELS[e.unit]}` : ""}`
          : "—"}
      </td>
      <td>{e ? <StatusChip status={e.status} /> : r.covered ? <span className="muted small">geërfd</span> : "—"}</td>
    </tr>
  );
}

function useDetail(code: string) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: detailKey(code),
    queryFn: () => api<LibraryDetailResponse>(`/platform/library/codes/${code}`),
  });
  const set = (d: LibraryDetailResponse) => {
    qc.setQueryData(detailKey(code), d);
    void qc.invalidateQueries({ queryKey: LIST_KEY, exact: true });
  };
  return { q, set };
}

function CodeDialog({ code, onClose }: { code: string; onClose: () => void }) {
  const { q, set } = useDetail(code);
  const d = q.data;
  return (
    <Modal title={d ? `${d.code} ${d.label}` : code} onClose={onClose} wide>
      {!d ? (
        <Loading />
      ) : (
        <div className="lib-detail">
          <p className="muted small">
            {d.assets} {d.assets === 1 ? "asset" : "assets"} met deze code
            {d.outdatedAssets > 0 && ` · ${d.outdatedAssets} met een oudere waarde uit de kennisbank`}
          </p>
          <ElementForm key={d.element?.version ?? 0} d={d} onSaved={set} />
          <Practice d={d} onSaved={set} />
        </div>
      )}
    </Modal>
  );
}

const elementBody = (d: LibraryDetailResponse, patch: Partial<LibraryElementBody> = {}): LibraryElementBody => ({
  lifespanYears: d.element?.lifespanYears ?? null,
  replacementPriceCents: d.element?.replacementPriceCents ?? null,
  unit: d.element?.unit ?? null,
  priceLevelYear: d.element?.priceLevelYear ?? null,
  status: d.element?.status ?? "concept",
  sourceTitle: d.element?.sourceTitle ?? null,
  sourceUrl: d.element?.sourceUrl ?? null,
  notes: d.element?.notes ?? null,
  ...patch,
});

function ElementForm({ d, onSaved }: { d: LibraryDetailResponse; onSaved: (d: LibraryDetailResponse) => void }) {
  const toast = useToast();
  const e = d.element;
  const [life, setLife] = useState(e?.lifespanYears?.toString() ?? "");
  const [price, setPrice] = useState(euroInput(e?.replacementPriceCents ?? null));
  const [unit, setUnit] = useState<LibraryUnit | "">(e?.unit ?? "");
  const [level, setLevel] = useState(e?.priceLevelYear?.toString() ?? "");
  const [status, setStatus] = useState<LibraryStatus>(e?.status ?? "concept");
  const [sourceTitle, setSourceTitle] = useState(e?.sourceTitle ?? "");
  const [sourceUrl, setSourceUrl] = useState(e?.sourceUrl ?? "");
  const [notes, setNotes] = useState(e?.notes ?? "");
  const save = useMutation({
    mutationFn: (body: LibraryElementBody) =>
      api<LibraryDetailResponse>(`/platform/library/codes/${d.code}`, { method: "PUT", body }),
    onSuccess: (r) => {
      onSaved(r);
      toast("Kennisbank bijgewerkt");
    },
    onError: (err) => toast(errMsg(err)),
  });
  const clear = useMutation({
    mutationFn: () => api<LibraryDetailResponse>(`/platform/library/codes/${d.code}`, { method: "DELETE" }),
    onSuccess: (r) => {
      onSaved(r);
      toast("Levensgegevens gewist");
    },
    onError: (err) => toast(errMsg(err)),
  });
  const badPrice = price.trim() !== "" && parseEuro(price) === null;
  const bad = badPrice || !validInt(life) || !validInt(level);
  const inherited = d.resolved;
  const lifeHint =
    !e?.lifespanYears && inherited.lifespanYears && inherited.lifespanYears.from !== d.code
      ? `geërfd van ${inherited.lifespanYears.from}: ${inherited.lifespanYears.value}`
      : "";
  const priceHint =
    !e?.replacementPriceCents && inherited.replacement && inherited.replacement.from !== d.code
      ? `geërfd van ${inherited.replacement.from}: ${euroInput(inherited.replacement.value.priceCents)}`
      : "";

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (bad) return;
    save.mutate({
      lifespanYears: intOrNull(life),
      replacementPriceCents: price.trim() ? parseEuro(price) : null,
      unit: unit || null,
      priceLevelYear: intOrNull(level),
      status,
      sourceTitle,
      sourceUrl,
      notes,
    });
  };

  return (
    <form className="lib-section" onSubmit={submit} aria-labelledby="lib-el">
      <div className="titlerow">
        <h3 id="lib-el">Levensgegevens</h3>
        {e?.reviewedAt && (
          <span className="muted small">Gecontroleerd op {new Date(e.reviewedAt).toLocaleDateString("nl-NL")}</span>
        )}
      </div>
      <div className="formgrid">
        <label className="field">
          <span className="label">Levensduur (jaar)</span>
          <input inputMode="numeric" value={life} placeholder={lifeHint} onChange={(x) => setLife(x.target.value)} />
        </label>
        <label className="field">
          <span className="label">Vervangingsprijs (€, excl. btw)</span>
          <input
            inputMode="decimal"
            value={price}
            placeholder={priceHint || "bijv. 25.000"}
            aria-invalid={badPrice}
            onChange={(x) => setPrice(x.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Eenheid</span>
          <select value={unit} onChange={(x) => setUnit(x.target.value as LibraryUnit | "")}>
            <option value="">—</option>
            {LIBRARY_UNITS.map((u) => (
              <option key={u} value={u}>
                {LIBRARY_UNIT_LABELS[u]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Prijspeil (jaar)</span>
          <input
            inputMode="numeric"
            value={level}
            placeholder={String(new Date().getFullYear())}
            onChange={(x) => setLevel(x.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Status</span>
          <select value={status} onChange={(x) => setStatus(x.target.value as LibraryStatus)}>
            {LIBRARY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {LIBRARY_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Bron</span>
          <input value={sourceTitle} onChange={(x) => setSourceTitle(x.target.value)} />
        </label>
        <label className="field">
          <span className="label">Bron-URL</span>
          <input type="url" value={sourceUrl} placeholder="https://" onChange={(x) => setSourceUrl(x.target.value)} />
        </label>
      </div>
      <label className="field">
        <span className="label">Opmerkingen</span>
        <textarea rows={2} value={notes} onChange={(x) => setNotes(x.target.value)} />
      </label>
      <div className="btnrow">
        <button type="submit" className="btn primary" disabled={save.isPending || bad}>
          Levensgegevens opslaan
        </button>
        {e && (
          <button
            type="button"
            className="btn danger-outline"
            disabled={clear.isPending}
            onClick={() => {
              if (confirm(`Levensgegevens van ${d.code} wissen? De code erft dan weer van de bovenliggende code.`))
                clear.mutate();
            }}
          >
            Gegevens wissen
          </button>
        )}
      </div>
    </form>
  );
}

function Practice({ d, onSaved }: { d: LibraryDetailResponse; onSaved: (d: LibraryDetailResponse) => void }) {
  const toast = useToast();
  const take = useMutation({
    mutationFn: (patch: Partial<LibraryElementBody>) =>
      api<LibraryDetailResponse>(`/platform/library/codes/${d.code}`, {
        method: "PUT",
        body: elementBody(d, { ...patch, status: "concept" }),
      }),
    onSuccess: (r) => {
      onSaved(r);
      toast("Overgenomen als concept");
    },
    onError: (e) => toast(errMsg(e)),
  });
  const { lifespanYears: life, replacementPriceCents: price } = d.practice;
  return (
    <section className="lib-section" aria-labelledby="lib-pr">
      <h3 id="lib-pr">Uit de praktijk</h3>
      {!life && !price ? (
        <p className="muted small">
          Nog geen mediaan: die verschijnt vanaf {PRACTICE_MIN_ORGS} organisaties met een waarde bij deze code.
        </p>
      ) : (
        <ul className="list">
          {life && (
            <li className="profile-row">
              <span className="grow">
                Levensduur: mediaan <strong>{life.median} jaar</strong>{" "}
                <span className="muted small">({life.orgs} organisaties)</span>
              </span>
              <button
                type="button"
                className="btn"
                disabled={take.isPending}
                onClick={() => take.mutate({ lifespanYears: life.median })}
              >
                Levensduur overnemen
              </button>
            </li>
          )}
          {price && (
            <li className="profile-row">
              <span className="grow">
                Vervangingsprijs: mediaan <strong>{formatEuro(price.median)}</strong>{" "}
                <span className="muted small">({price.orgs} organisaties)</span>
              </span>
              <button
                type="button"
                className="btn"
                disabled={take.isPending}
                onClick={() => take.mutate({ replacementPriceCents: price.median })}
              >
                Prijs overnemen
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

const CHANGE_LABELS = { new: "Nieuw", changed: "Gewijzigd", invalid: "Ongeldig" } as const;

function ImportDialog({ file, name, onClose }: { file: string; name: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const preview = useQuery({
    queryKey: ["platform", "library-import", name, file.length],
    queryFn: () => api<LibraryImportResult>("/platform/library/import", { method: "POST", body: { file } }),
    retry: false,
    gcTime: 0,
  });
  const apply = useMutation({
    mutationFn: () =>
      api<LibraryImportResult>("/platform/library/import", { method: "POST", body: { file, confirm: true } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: LIST_KEY });
      toast(`Kennisbank bijgewerkt: ${r.counts.new} nieuw, ${r.counts.changed} gewijzigd`);
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  const r = preview.data;
  const nothing = r && r.counts.new + r.counts.changed === 0;
  return (
    <Modal
      title="Import controleren"
      onClose={onClose}
      wide
      busy={apply.isPending}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!r || r.counts.invalid > 0 || nothing || apply.isPending}
            onClick={() => apply.mutate()}
          >
            Wijzigingen doorvoeren
          </button>
        </>
      }
    >
      <p className="muted small">{name}</p>
      {preview.isPending ? (
        <p className="muted">Bestand lezen…</p>
      ) : preview.error ? (
        <p role="alert">{errMsg(preview.error)}</p>
      ) : r ? (
        <>
          <p data-testid="import-counts">
            {r.counts.new} nieuw · {r.counts.changed} gewijzigd · {r.counts.unchanged} ongewijzigd · {r.counts.invalid}{" "}
            ongeldig
          </p>
          {r.counts.invalid > 0 && (
            <p role="alert" className="lib-warn">
              Los eerst de ongeldige rijen op in Excel en importeer opnieuw. Er is nog niets opgeslagen.
            </p>
          )}
          {nothing && !r.counts.invalid && <p className="muted">Er verandert niets.</p>}
          {r.changes.length > 0 && (
            <div className="tscroll">
              <table className="it">
                <thead>
                  <tr>
                    <th>Blad</th>
                    <th className="num">Rij</th>
                    <th>Code</th>
                    <th>Wat</th>
                    <th>Toelichting</th>
                  </tr>
                </thead>
                <tbody>
                  {r.changes.slice(0, 300).map((c) => (
                    <tr key={`${c.sheet}-${c.row}`} className={c.kind === "invalid" ? "lib-invalid" : ""}>
                      <td>Elementen</td>
                      <td className="num">{c.row}</td>
                      <td className="num">{c.code || "—"}</td>
                      <td>{CHANGE_LABELS[c.kind]}</td>
                      <td>
                        {c.name && <strong>{c.name}: </strong>}
                        {c.detail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </Modal>
  );
}
